import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";

const ContracteZoho = () => {
  const apiBase = useMemo(() => {
    return process.env.REACT_APP_API_BASE || "https://server-9p4hx.kinsta.app";
  }, []);

  const templateId = "104126000000048141";

  const [signerEmail, setSignerEmail] = useState("");
  const [templateFields, setTemplateFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState("");

  const [loading, setLoading] = useState(false);
  const [requestIdState, setRequestIdState] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let isActive = true;
    setTemplateLoading(true);
    setTemplateError("");

    fetch(`${apiBase}/api/contracte/zoho/templates/${templateId}/fields`)
      .then((resp) => resp.json())
      .then((json) => {
        if (!isActive) return;
        if (!json?.ok) {
          throw new Error(json?.message || "Nu am putut incarca template-ul.");
        }

        const rawFields = Array.isArray(json.text_fields)
          ? json.text_fields
          : [];
        const seen = new Map();
        rawFields.forEach((field) => {
          const name = String(field?.field_name || "").trim();
          if (!name) return;
          if (!seen.has(name)) {
            seen.set(name, {
              field_name: name,
              field_label: field?.field_label || name,
              is_mandatory: Boolean(field?.is_mandatory),
            });
          }
        });

        const list = Array.from(seen.values());
        setTemplateFields(list);
        setFieldValues((prev) => {
          const next = { ...prev };
          list.forEach((field) => {
            if (next[field.field_name] === undefined) {
              next[field.field_name] = "";
            }
          });
          return next;
        });
      })
      .catch((err) => {
        if (!isActive) return;
        setTemplateError(err?.message || "Nu am putut incarca template-ul.");
      })
      .finally(() => {
        if (!isActive) return;
        setTemplateLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [apiBase, templateId]);

  const clientName = `${fieldValues.nume || ""} ${fieldValues.prenume || ""}`
    .trim()
    .replace(/\s+/g, " ");

  const handleFieldChange = (fieldName) => (event) => {
    const value = event.target.value;
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  function resetForNextPerson() {
    setSignerEmail("");
    setFieldValues((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = "";
      });
      return next;
    });
  }

  async function handleSend() {
    setError("");
    setResult(null);
    setRequestIdState("");

    if (!signerEmail) {
      setError("Te rog completeaza Emailul semnatarului.");
      return;
    }

    const missing = templateFields.find(
      (field) =>
        field.is_mandatory &&
        !String(fieldValues[field.field_name] || "").trim(),
    );
    if (missing) {
      setError(
        `Camp obligatoriu lipsa: ${missing.field_label || missing.field_name}`,
      );
      return;
    }

    try {
      setLoading(true);

      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
        now.getDate(),
      ).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes(),
      ).padStart(2, "0")}`;

      const payload = {
        fields: fieldValues,
        signer_email: signerEmail,
        signer_name: clientName || "Client",
        request_name: `Contract - ${clientName || signerEmail} - ${stamp}`,
        auto_submit: true,
      };

      const createRes = await fetch(
        `${apiBase}/api/contracte/zoho/templates/${templateId}/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const createJson = await createRes.json().catch(() => null);

      if (!createRes.ok || !createJson?.ok) {
        const msg =
          createJson?.message ||
          `Eroare la /templates/${templateId}/create (${createRes.status})`;
        throw new Error(msg);
      }

      const rid = createJson.request_id;
      if (!rid) throw new Error("Backend nu a returnat request_id.");

      setRequestIdState(rid);

      setResult({
        message:
          "Trimis la semnat. Clientul primeste primul invitatia; financiar primeste automat dupa ce clientul semneaza. Dupa semnarea completa, backend-ul trimite automat email de confirmare cu PDF atasat catre client si financiar.",
        create: createJson,
      });

      resetForNextPerson();
    } catch (e) {
      setError(e?.message || "A aparut o eroare.");
    } finally {
      setLoading(false);
    }
  }

  if (templateLoading) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Contracte (Zoho Sign)
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Se incarca template-ul...</Typography>
        </Box>
      </Box>
    );
  }

  if (templateError) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Contracte (Zoho Sign)
        </Typography>
        <Alert severity="error">{templateError}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Contracte (Zoho Sign)
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Completeaza campurile template-ului Zoho si apasa Send. Clientul
        semneaza primul; financiar semneaza dupa client; la final se trimite
        automat PDF-ul semnat pe email la amandoi.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      {result ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          {result.message}
          {requestIdState ? (
            <Box sx={{ mt: 1, fontFamily: "monospace", fontSize: 12 }}>
              request_id: {requestIdState}
            </Box>
          ) : null}
        </Alert>
      ) : null}

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        sx={{
          mt: 3,
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <TextField
          label="Email semnatar"
          name="signerEmail"
          type="email"
          value={signerEmail}
          onChange={(e) => setSignerEmail(e.target.value)}
          fullWidth
        />

        {templateFields.map((field) => (
          <TextField
            key={field.field_name}
            label={field.field_label || field.field_name}
            name={field.field_name}
            value={fieldValues[field.field_name] || ""}
            onChange={handleFieldChange(field.field_name)}
            fullWidth
            required={field.is_mandatory}
          />
        ))}

        <Box
          sx={{
            gridColumn: { xs: "1", md: "1 / -1" },
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
          >
            {loading ? "Se trimite..." : "Send"}
          </Button>
          {loading ? <CircularProgress size={22} /> : null}

          {requestIdState ? (
            <>
              <Button
                variant="outlined"
                disabled={loading}
                onClick={() => {
                  window.open(
                    `${apiBase}/api/contracte/zoho/requests/${encodeURIComponent(requestIdState)}`,
                    "_blank",
                  );
                }}
              >
                Status (JSON)
              </Button>

              <Button
                variant="outlined"
                disabled={loading}
                onClick={() => {
                  window.open(
                    `${apiBase}/api/contracte/zoho/requests/${encodeURIComponent(requestIdState)}/pdf`,
                    "_blank",
                  );
                }}
              >
                Download PDF (daca e complet)
              </Button>
            </>
          ) : null}

          <Button
            variant="text"
            disabled={loading}
            onClick={() => {
              setError("");
              setResult(null);
              setRequestIdState("");
              resetForNextPerson();
            }}
          >
            Reset form
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ContracteZoho;
